import { HomeIcon, MusicIcon, NewspaperIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { twMerge } from 'tailwind-merge';
import { useBlogTrans } from '../blogHooks/useBlogTrans';
import blogSidebarStore from '../blogStores/blogSidebarStore';
import Frame from '../uikit/Frame';
import { useBlogContext } from './BlogProvider';
import { BlogLangDropdown } from './home/BlogLangButton';

const BlogSidebar = observer(() => {
    const [isMounted, setIsMounted] = useState(false);

    const location = useLocation();
    const prevLocation = useRef(location);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    /**
     * 라우팅이 바뀌면 사이드바를 닫음
     */
    useEffect(() => {
        if (prevLocation.current !== location) {
            blogSidebarStore.closeSidebar();
        }
        prevLocation.current = location;
    }, [location]);

    if (!isMounted) {
        return null;
    }

    return (
        <div className="drawer-side z-(--z-index-sidebar)">
            <label htmlFor="sidebar" aria-label="close sidebar" className="drawer-overlay"></label>
            <div className="bg-base-100 h-full w-60">
                <BlogSidebarContent />
            </div>
        </div>
    );
});

export const BlogSidebarContent = observer(() => {
    const location = useLocation();
    const { lang } = useBlogContext();
    const { trans } = useBlogTrans();

    return (
        <Frame>
            <ul className="menu min-h-full w-full gap-1 text-base">
                <li className="menu-item">
                    <NavLink
                        to={`/${lang}`}
                        // 루트 경로는 항상 매칭되므로 별도로 처리
                        className={twMerge(
                            location.pathname === `/${lang}` && 'text-primary font-semibold',
                        )}
                        preventScrollReset={false}
                    >
                        <HomeIcon className="size-4 stroke-2 opacity-60" />
                        {trans('home')}
                    </NavLink>
                </li>
                <li className="menu-item">
                    <NavLink
                        to={`/${lang}/post/economic-news`}
                        className={({ isActive }) =>
                            twMerge(isActive && 'text-primary font-semibold')
                        }
                        preventScrollReset={false}
                    >
                        <NewspaperIcon className="size-4 stroke-2 opacity-60" />
                        {trans('economic-news')}
                    </NavLink>
                    <NavLink
                        to={`/${lang}/post/classical-music`}
                        className={({ isActive }) =>
                            twMerge(isActive && 'text-primary font-semibold')
                        }
                        preventScrollReset={false}
                    >
                        <MusicIcon className="size-4 stroke-2 opacity-60" />
                        {trans('classical-music')}
                    </NavLink>
                </li>
            </ul>
            <Frame.Spacer />
            <ul className="menu w-full">
                <li className="menu-item">
                    <NavLink to={`/${lang}/terms`} className="text-sm" preventScrollReset={false}>
                        {trans('terms')}
                    </NavLink>
                </li>
                <li className="menu-item">
                    <NavLink to={`/${lang}/privacy`} className="text-sm" preventScrollReset={false}>
                        {trans('privacy')}
                    </NavLink>
                </li>
            </ul>
            <div className="mb-5 ml-5">
                <BlogLangDropdown direction="top" />
            </div>
        </Frame>
    );
});

export const BlogSidebarButton = observer(
    ({ className, children }: { className?: string; children: React.ReactNode }) => {
        return (
            <label htmlFor="sidebar" className={className}>
                {children}
            </label>
        );
    },
);

export default BlogSidebar;
